import { Button, Checkbox, FormControlLabel, Grid, TextField, ToggleButtonGroup } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { titleize } from "inflection";
import { filter, isEmpty, isEqual, map, uniq } from "lodash";
import { debounce as fpDebounce, map as fpMap } from "lodash/fp";
import React, { useContext, useRef, useState } from "react";

import { StyledToggleButton } from "./StyledComponents";
import { useQuery, useMutation } from "@apollo/client/react";
import type { DiaryEntryModalProps } from "./types";
import { Attachment } from "@mui/icons-material";
import OverwatchModal from "@/components/OverwatchModal";
import TimeAutocomplete from "@/components/TimeAutocomplete";
import UploadPreviewItem from "@/components/UploadPreviewItem";
import { ReportContext } from "@/Contexts/ReportContext";
import { IssueTypeEnum, GetIssueCategoriesDocument, UpdateReportIssueDocument, CreateReportIssueDocument, GetReportIssuesDocument } from "@/graphql/generated/graphql";
import useFileUploader from "@/hooks/useFileUploader";
import TextEditor from "@/TextEditor";
import { VisuallyHiddenInput } from "@/theme/styled-components";
import { ACCEPTED_ATTACHMENT_FORMATS } from "@/utils/constants";
import { getInUTC } from "@/utils/dateTimeHelpers";
import { displayNotification } from "@/utils/notifications";

const mapId = fpMap("id");
const mapSignedBlobId = fpMap("signedBlobId");

const DiaryEntryModal: React.FC<DiaryEntryModalProps> = ({
  handleClose,
  issue,
  editMode = false,
}) => {
  const { selectedReport } = useContext(ReportContext);
  const debounce = useRef(fpDebounce(500)(fn => fn())).current;

  const [diaryHeadline, setDiaryHeadline] = useState<string | null>(
    editMode && issue ? issue.headline as string : null
  );
  const [diaryText, setDiaryText] = useState<string | null>(
    editMode && issue ? issue.description as string : null
  );
  const [diaryType, setDiaryType] = useState(
    editMode && issue ? issue.issueType : IssueTypeEnum.InternalIssue
  );
  const [diaryTags, setDiaryTags] = useState<Array<string>>(
    editMode ? mapId(issue?.categories) : []
  );
  const [diaryStartTime, setDiaryStartTime] = useState<Dayjs | null>(
    editMode ? dayjs(issue?.startTime).utc() : dayjs().set("hour", 9).set("minute", 0).set("second", 0)
  );
  const [diaryEndTime, setDiaryEndTime] = useState<Dayjs | null>(
    editMode ? dayjs(issue?.endTime).utc() : dayjs().set("hour", 17).set("minute", 0).set("second", 0)
  );
  const [diaryFlagged, setDiaryFlagged] = useState<boolean>(
    editMode ? issue?.flagged ?? false : false
  );
  const { isUploading, files, handleUploadImage, handleRemoveImage, shouldDisableUploadButton } = useFileUploader();

  const { data } = useQuery(GetIssueCategoriesDocument, { variables: { dynamic: false }, });
  const issueCategories = data?.getIssueCategories;

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    onCompleted: () => handleClose(),
  });

  const [createReportIssue] = useMutation(CreateReportIssueDocument, {
    onCompleted: () => handleClose(),
  });

  const handleCreate = () => {
    createReportIssue({
      variables: {
        reportIssue: {
          ...(diaryHeadline && { headline: diaryHeadline }),
          reportId: selectedReport?.id as string,
          description: diaryText as string,
          categoryNames: diaryTags,
          issueType: diaryType as IssueTypeEnum,
          startTime: getInUTC(diaryStartTime as Dayjs),
          endTime: getInUTC(diaryEndTime as Dayjs),
          signedIds: mapSignedBlobId(files),
          flagged: diaryFlagged,
        },
      },
      onCompleted: () => {
        displayNotification({
          type: "success",
          text: "Entry added successfully",
        });
        handleClose();
      },
      update(cache, { data }) {
        const createdIssue = data?.createReportIssue;
        const reportId = selectedReport?.id;

        if (!reportId || !createdIssue) return;

        const existingIssues =
          cache.readQuery({
            query: GetReportIssuesDocument,
            variables: { reportId },
          })?.getReportIssues ?? [];

        cache.writeQuery({
          query: GetReportIssuesDocument,
          variables: { reportId },
          data: {
            getReportIssues: [
              ...existingIssues,
              ...filter([createdIssue], (issue) => !existingIssues.some((existingIssue) => existingIssue.id === issue.id)),
            ],
          },
        });
      },
    });
  };

  const handleUpdateReportIssue = () => {
    updateIssue({
      variables: {
        reportIssue: {
          id: issue?.id as string,
          ...(diaryHeadline && { headline: diaryHeadline }),
          ...(diaryText && { description: diaryText }),
          ...(diaryTags && { categoryIds: diaryTags }),
          ...(diaryType && { issueType: diaryType }),
          ...(diaryStartTime && { startTime: diaryStartTime?.format("HH:mm") }),
          ...(diaryEndTime && { endTime: diaryEndTime?.format("HH:mm") }),
          flagged: diaryFlagged, // can't have a sanity check as it defaults to 'false' if absent in edit mode
        },
      },
      update(cache) {
        const reportId = selectedReport?.id;
        if (!reportId || !issue?.id) return;

        const existingIssues =
          cache.readQuery({
            query: GetReportIssuesDocument,
            variables: { reportId },
          })?.getReportIssues ?? [];

        cache.writeQuery({
          query: GetReportIssuesDocument,
          variables: { reportId },
          data: {
            getReportIssues: map(existingIssues, (existingIssue) => (
              existingIssue.id === issue.id
                ? {
                  ...existingIssue,
                  ...(diaryHeadline !== null && { headline: diaryHeadline }),
                  ...(diaryText !== null && { description: diaryText }),
                  ...(diaryType && { issueType: diaryType }),
                  ...(diaryStartTime && { startTime: diaryStartTime.toISOString() }),
                  ...(diaryEndTime && { endTime: diaryEndTime.toISOString() }),
                  ...(diaryTags && {
                    categories: filter(
                      issueCategories || [],
                      (category) => diaryTags.includes(category.id)
                    ),
                  }),
                  ...(diaryFlagged !== undefined && { flagged: diaryFlagged }),
                }
                : existingIssue
            )),
          },
        });
      },
    });
  };

  const hasChanges = () => {
    if (!editMode || !issue) return true;

    const isHeadlineChanged = diaryHeadline !== null && diaryHeadline !== issue.headline;
    const isTextChanged = diaryText !== null && diaryText !== issue.description;
    const isTypeChanged = diaryType !== null && diaryType !== issue.issueType;
    const isTagsChanged = !isEmpty(diaryTags) && !isEqual(diaryTags, map(issue.categories, "id"));
    const isStartTimeChanged = diaryStartTime && diaryStartTime.format("HH:mm") !== dayjs(issue.startTime).utc().format("HH:mm");
    const isEndTimeChanged = diaryEndTime && diaryEndTime.format("HH:mm") !== dayjs(issue.endTime).utc().format("HH:mm");
    const isFlaggedChanged = diaryFlagged !== undefined && diaryFlagged !== issue.flagged;

    return (
      isHeadlineChanged || isTextChanged || isTypeChanged || isTagsChanged || isStartTimeChanged || isEndTimeChanged || isFlaggedChanged
    );
  };

  const handleTimeUpdate = (
    newTimeStr: string,
    currentDate: Dayjs | null,
    setter: (date: Dayjs) => void
  ) => {
    if (!newTimeStr) return;

    const [hours, minutes] = newTimeStr.split(':').map(Number);

    if (!isNaN(hours) && !isNaN(minutes)) {
      const baseDate = currentDate || dayjs();
      const updatedDate = baseDate.hour(hours).minute(minutes).second(0);
      setter(updatedDate);
    }
  };

  return (
    <OverwatchModal
      title={`${editMode ? "Edit" : "Add"} Diary Entry`}
      handleClose={() => {
        if (editMode) {
          handleUpdateReportIssue()
        } else {
          handleCreate();
        }
        handleClose();
      }}
      size="md"
      primaryButtonText="Save"
      primaryButtonAction={editMode ? handleUpdateReportIssue : handleCreate}
      disablePrimary={editMode ? !hasChanges() : !diaryText || !diaryType}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
    >
      <Grid container mt={1} mb={1} justifyContent="space-between">
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 0, mb: 2 }}
          placeholder="Untitled Diary Entry"
          label="Headline"
          helperText="Optionally set a title for the diary entry"
          defaultValue={issue?.headline}
          onChange={(e) => {
            debounce(() => setDiaryHeadline(e.target.value))
          }}
        />
        <ToggleButtonGroup
          value={diaryType || issue?.issueType}
          exclusive
          onChange={(_, newSelection) => setDiaryType(newSelection)}
          color="primary"
          sx={{ border: `1px solid ${(!diaryType && !issue?.issueType) ? "red" : "primary.main"}` }}
        >
          {Object.keys(IssueTypeEnum).map((type) => (
            <StyledToggleButton
              size="small"
              key={IssueTypeEnum[type as keyof typeof IssueTypeEnum]}
              value={IssueTypeEnum[type as keyof typeof IssueTypeEnum]}
            >
              {titleize(type.replace("Issue", ""))}
            </StyledToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
      <TextEditor
        setText={(val: string) => {
          const regex = /data-label="([^"]*)"/g;
          const categoriesMentioned = uniq([...val.matchAll(regex)].map(match => match[1])) // ['Work Done', 'Quality'] etc...
          const categoryIds = mapId(filter(issueCategories, category => categoriesMentioned.includes(category.name)));

          setDiaryText(val);
          setDiaryTags(categoryIds);
        }}
        text={diaryText || (issue?.description as string) || ""}
        options='IssueCategory'
        renderToolbar
        mentionChar="#"
      />
      <Grid container size={6} spacing={1} mt={2}>
        <Grid size={3}>
          <TimeAutocomplete
            label="Start Time"
            // Convert Dayjs -> String for the input
            time={diaryStartTime ? diaryStartTime.format('HH:mm') : ''}
            // Convert String -> Dayjs for the state
            onTimeChange={(newTime: string) => handleTimeUpdate(newTime, diaryStartTime, setDiaryStartTime)}
            error={!diaryStartTime}
            helperText={!diaryStartTime ? "Start Time is required" : ""}
          />
        </Grid>
        <Grid size={3}>
          <TimeAutocomplete
            label="End Time"
            // Convert Dayjs -> String for the input
            time={diaryEndTime ? diaryEndTime.format('HH:mm') : ''}
            // Convert String -> Dayjs for the state
            onTimeChange={(newTime: string) => handleTimeUpdate(newTime, diaryEndTime, setDiaryEndTime)}
            error={!diaryEndTime}
            helperText={!diaryEndTime ? "End Time is required" : ""}
          />
        </Grid>
      </Grid>
      <Button
        component="label"
        variant="contained"
        tabIndex={-1}
        startIcon={<Attachment />}
        sx={{ marginTop: 1 }}
        disabled={shouldDisableUploadButton}
      >
        Attach
        <VisuallyHiddenInput
          type="file"
          accept={ACCEPTED_ATTACHMENT_FORMATS}
          onChange={handleUploadImage}
          disabled={shouldDisableUploadButton}
        />
      </Button>
      {!isEmpty(files) && (
        <Grid size={12}>
          {!isEmpty(files) &&
            map(files, (attachment, index) => (
              <UploadPreviewItem
                file={attachment}
                isUploading={isUploading}
                key={`${index}-${attachment.id}`}
                onRemove={() => handleRemoveImage(attachment.id)}
              />
            ))
          }
        </Grid>
      )}
      <Grid size={12}>
        <FormControlLabel
          control={<Checkbox checked={diaryFlagged} onChange={(_, checked) => setDiaryFlagged(checked)} />}
          label="Flag as a Potential Event"
        />
      </Grid>
    </OverwatchModal >
  );
};

export default DiaryEntryModal;
